import { useRef, useState } from 'react';

interface QuickReplyButtonsProps {
  onSelect: (text: string) => void;
}

const categories = [
  {
    label: '挨拶',
    icon: 'ri-sun-line',
    activeColor: 'bg-amber-400/40 border-amber-400/50',
    iconColor: 'rgba(251,191,36,1)',
    replies: [
      { text: 'おはよう☀️', icon: 'ri-sun-line' },
      { text: 'こんにちは', icon: 'ri-chat-smile-line' },
      { text: 'こんばんは🌙', icon: 'ri-moon-line' },
      { text: 'ただいま', icon: 'ri-home-heart-line' },
      { text: 'おやすみ', icon: 'ri-moon-foggy-line' },
      { text: 'ちょっと話しかけたくなった', icon: 'ri-chat-3-line' },
    ],
  },
  {
    label: '嬉しい',
    icon: 'ri-emotion-happy-line',
    activeColor: 'bg-pink-400/40 border-pink-400/50',
    iconColor: 'rgba(251,113,133,1)',
    replies: [
      { text: '嬉しいことあった！', icon: 'ri-star-smile-line' },
      { text: 'テンション上がってる', icon: 'ri-flashlight-line' },
      { text: 'うまくいった！', icon: 'ri-trophy-line' },
      { text: '褒めて', icon: 'ri-award-line' },
      { text: '聞いて聞いて！', icon: 'ri-megaphone-line' },
      { text: '最高な気分', icon: 'ri-sparkling-2-line' },
    ],
  },
  {
    label: '疲れた',
    icon: 'ri-zzz-line',
    activeColor: 'bg-slate-400/40 border-slate-400/50',
    iconColor: 'rgba(148,163,184,1)',
    replies: [
      { text: '疲れた…', icon: 'ri-zzz-line' },
      { text: 'しんどい', icon: 'ri-heart-pulse-line' },
      { text: '仕事大変だった', icon: 'ri-briefcase-line' },
      { text: '癒して', icon: 'ri-heart-3-line' },
      { text: 'HP減ってる', icon: 'ri-battery-low-line' },
      { text: 'ゆっくりしたい', icon: 'ri-leaf-line' },
    ],
  },
  {
    label: '不安',
    icon: 'ri-mental-health-line',
    activeColor: 'bg-violet-400/40 border-violet-400/50',
    iconColor: 'rgba(167,139,250,1)',
    replies: [
      { text: '不安なんだ', icon: 'ri-mental-health-line' },
      { text: 'ドキドキしてる', icon: 'ri-heart-pulse-line' },
      { text: '心配なことがある', icon: 'ri-question-answer-line' },
      { text: '緊張してる', icon: 'ri-shake-hands-line' },
      { text: '相談がある', icon: 'ri-discuss-line' },
      { text: 'どうしよう…', icon: 'ri-error-warning-line' },
    ],
  },
  {
    label: '悲しい',
    icon: 'ri-drop-line',
    activeColor: 'bg-sky-400/40 border-sky-400/50',
    iconColor: 'rgba(56,189,248,1)',
    replies: [
      { text: '悲しい', icon: 'ri-drop-line' },
      { text: '寂しい', icon: 'ri-user-heart-line' },
      { text: '泣きそう', icon: 'ri-emotion-sad-line' },
      { text: '甘えたい', icon: 'ri-heart-3-line' },
      { text: '話を聞いて', icon: 'ri-ear-line' },
      { text: 'そばにいて', icon: 'ri-hand-heart-line' },
    ],
  },
  {
    label: 'イライラ',
    icon: 'ri-fire-line',
    activeColor: 'bg-orange-400/40 border-orange-400/50',
    iconColor: 'rgba(251,146,60,1)',
    replies: [
      { text: 'イライラしてる', icon: 'ri-fire-line' },
      { text: 'むかつく', icon: 'ri-emotion-unhappy-line' },
      { text: 'ちょっと聞いて', icon: 'ri-megaphone-line' },
      { text: '愚痴聞いて', icon: 'ri-chat-voice-line' },
      { text: '最悪な日だった', icon: 'ri-thunderstorms-line' },
      { text: 'ストレス溜まってる', icon: 'ri-mental-health-line' },
    ],
  },
];

export default function QuickReplyButtons({ onSelect }: QuickReplyButtonsProps) {
  const [activeCategory, setActiveCategory] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const dragDistance = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = false;
    dragDistance.current = 0;
    startX.current = e.pageX - (scrollRef.current?.offsetLeft ?? 0);
    scrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    dragDistance.current = Math.abs(walk);
    if (dragDistance.current > 5) {
      isDragging.current = true;
      scrollRef.current.scrollLeft = scrollLeft.current - walk;
    }
  };

  const handleMouseUp = () => {
    setTimeout(() => { isDragging.current = false; }, 50);
  };

  const currentCategory = categories[activeCategory];

  return (
    <div className="bg-white/20 backdrop-blur-md border-t border-white/25 pt-1.5 pb-1">
      {/* カテゴリタブ：アイコンのみ・コンパクト */}
      <div className="flex gap-1 px-2 mb-1.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {categories.map((cat, i) => (
          <button
            key={i}
            onClick={() => setActiveCategory(i)}
            title={cat.label}
            className={`flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap border ${
              activeCategory === i
                ? `${cat.activeColor} shadow-sm`
                : 'bg-white/35 border-white/40 hover:bg-white/50'
            }`}
            style={{ color: activeCategory === i ? 'rgba(10,50,30,0.9)' : 'rgba(50,50,50,0.75)' }}
          >
            <span className="w-3 h-3 flex items-center justify-center">
              <i
                className={`${cat.icon} text-[11px]`}
                style={{ color: activeCategory === i ? cat.iconColor : 'rgba(120,120,120,0.8)' }}
              ></i>
            </span>
            {cat.label}
          </button>
        ))}
      </div>

      {/* リプライボタン：コンパクト */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-black/8 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-black/8 to-transparent z-10 pointer-events-none" />
        <div
          ref={scrollRef}
          className="flex gap-1.5 overflow-x-auto px-3 select-none"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {currentCategory.replies.map((reply, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isDragging.current) onSelect(reply.text);
              }}
              className="flex-shrink-0 bg-white/55 hover:bg-white/75 backdrop-blur-sm px-2.5 py-1 rounded-full text-[11px] font-medium border border-white/55 hover:border-white/75 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 shadow-sm hover:shadow-md active:scale-95"
              style={{ color: 'rgba(30,30,30,0.85)' }}
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i
                  className={`${reply.icon} text-[11px]`}
                  style={{ color: currentCategory.iconColor }}
                ></i>
              </span>
              {reply.text}
            </button>
          ))}
        </div>
      </div>

      <style>{`div::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
}
